import React, { useCallback, useEffect, useState } from 'react'
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridSortModel,
  gridClasses,
  GridRowParams,
  GridPaginationModel,
} from '@mui/x-data-grid'
import {
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material'
import { ptBR } from '@mui/x-data-grid/locales'
import { formatDateHour } from '../../../../utils/format'
import { MergeType } from '@mui/icons-material'
import { useLang } from '../../../../hooks/useLang'
import { authFetch } from '~/services/fetch'
import useDispatchState from '../../stores/DispatchState'
import { useHookstate } from '@hookstate/core'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorFallback from '~/components/ErrorFallback'
import useSystemState from '~/features/System/stores/SystemState'
import notify from '~/utils/notify'
import DispatchUnificationFilters from './DispatchUnificationFilters'

// Tipos
interface DispatchRow {
  readonly id: number
  readonly code: string
  readonly occurrence?: {
    readonly location?: string
  }
  readonly dispatched_at?: string
  readonly type?: {
    readonly name?: string
  }
  readonly subtype?: {
    readonly name?: string
  }
}

interface ApiResponse {
  readonly data: readonly DispatchRow[]
  readonly rowsCount: number
}

interface Filters {
  readonly code: string
  readonly location: string
  readonly type_id: string
  readonly subtype_id: string
  readonly start_date: string
  readonly finish_date: string
}

// eslint-disable-next-line max-lines-per-function
export default function DispatchUnificationTable() {
  const { translate } = useLang()
  const { windowManager } = useSystemState()
  const dispatchState = useDispatchState()
  const { status } = dispatchState.dispatch()

  // Estados do Hookstate
  const loadingButton = useHookstate<boolean>(false)
  const openDialog = useHookstate<boolean>(false)

  // Estados React
  const [rows, setRows] = useState<readonly DispatchRow[]>([])
  const [rowCount, setRowCount] = useState(0)
  const [dispatchId, setDispatchId] = useState<number>(0)
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: 5,
  })
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'created_at', sort: 'desc' },
  ])

  const [filters, setFilters] = useState<Filters>({
    code: '',
    location: '',
    type_id: '',
    subtype_id: '',
    start_date: '',
    finish_date: '',
  })

  const tableMode = 'server'
  const limit = 5

  const handleClose = () => {
    openDialog.set(false)
  }

  const action = () => {
    dispatchState.unificationDispatch({ dispatchId: dispatchId })
    windowManager()['DispatchUnification'].open.set(false)
    openDialog.set(false)
    notify({
      type: 'success',
      message: translate('Dispatch unified successfully!'),
    })
  }

  const unification = useCallback(
    (row: DispatchRow) => () => {
      setDispatchId(row.id)
      openDialog.set(true)
    },
    []
  )

  const columns: readonly GridColDef[] = [
    {
      field: 'id',
      headerName: translate('Id'),
      flex: 0.5,
      filterable: false,
      sortable: false,
    },
    {
      field: 'code',
      headerName: translate('Code'),
      flex: 1,
      filterable: false,
      sortable: false,
    },
    {
      field: 'occurrence:location',
      headerName: translate('Location'),
      flex: 1.5,
      filterable: false,
      sortable: false,
      valueGetter: (value: unknown, row: DispatchRow) => row.occurrence?.location || '-',
    },
    {
      field: 'dispatched_at',
      headerName: translate('Date'),
      flex: 1.2,
      filterable: false,
      sortable: true,
      valueFormatter: (value: unknown) => {
        if (!value) return '-'
        return formatDateHour(value as string).toFormatedDate
      },
    },
    {
      field: 'type:name',
      headerName: translate('Type'),
      flex: 1.5,
      filterable: false,
      sortable: false,
      valueGetter: (value: unknown, row: DispatchRow) => row.type?.name || '-',
    },
    {
      field: 'subtype:name',
      headerName: translate('Subtype'),
      flex: 1,
      filterable: false,
      sortable: false,
      valueGetter: (value: unknown, row: DispatchRow) => row.subtype?.name || '-',
    },
    {
      field: 'actions',
      type: 'actions',
      flex: 0.5,
      align: 'center',
      getActions: ({ row }: GridRowParams<DispatchRow>) => [
        <GridActionsCellItem
          disabled={status ? status.id.get() !== 1 : false}
          key={`${row.id}-unify`}
          icon={
            <Tooltip title={translate('Unify')}>
              <MergeType />
            </Tooltip>
          }
          label="unify"
          onClick={unification(row)}
        />,
      ],
    },
  ]

  const fetchData = useCallback(
    async (
      model: GridPaginationModel = paginationModel,
      currentSortModel: GridSortModel = sortModel
    ) => {
      const dispatchId = dispatchState.dispatch().id.get()
      
      if (!dispatchId) {
        console.warn('Dispatch ID não está disponível ainda')
        return
      }

      loadingButton.set(true)

      try {
        const orders = currentSortModel.map(({ field, sort }) => ({
          field: field === 'dispatched_at' ? 'dispatches.created_at' : field,
          sort: sort || 'desc',
        }))

        const payload = {
          dispatch_id: dispatchId,
          orders:
            orders.length > 0
              ? orders
              : [{ field: 'dispatches.created_at', sort: 'desc' }],
          page: model.page,
          limit: model.pageSize,
          code: filters.code,
          location: filters.location,
          type_id: filters.type_id,
          subtype_id: filters.subtype_id,
          start_date: filters.start_date,
          finish_date: filters.finish_date,
        }
        
        const { data: responseData }: { readonly data: ApiResponse } = await authFetch({
          url: `/dispatch/list-to-unify`,
          method: 'POST',
          data: payload,
        })

        setRows(responseData.data || [])
        setRowCount(responseData.rowsCount || 0)
      } catch (error) {
        console.error('Erro ao carregar lista de despachos:', error)
        notify({
          type: 'error',
          message: translate('Error loading dispatch list'),
        })
        setRows([])
        setRowCount(0)
      } finally {
        loadingButton.set(false)
      }
    },
    [filters]
  )

  useEffect(() => {
    const dispatchId = dispatchState.dispatch().id.get()
    
    if (dispatchId) {
      fetchData(paginationModel, sortModel)
    }
  }, [dispatchState.dispatch().id.get()])

  const handlePaginationChange = (model: GridPaginationModel) => {
    setPaginationModel(model)
    fetchData(model, sortModel)
  }

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model)
    const firstPageModel: GridPaginationModel = { page: 0, pageSize: paginationModel.pageSize }
    setPaginationModel(firstPageModel)
    fetchData(firstPageModel, model)
  }

  const clearFilters = () => {
    setFilters({
      code: '',
      location: '',
      type_id: '',
      subtype_id: '',
      start_date: '',
      finish_date: '',
    })
    setPaginationModel({ page: 0, pageSize: limit })
    setSortModel([{ field: 'created_at', sort: 'desc' }])
  }

  const searchFromFirstPage = () => {
    const firstPageModel: GridPaginationModel = { page: 0, pageSize: paginationModel.pageSize }
    setPaginationModel(firstPageModel)
    fetchData(firstPageModel, sortModel)
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <DispatchUnificationFilters
        filters={filters}
        onFiltersChange={setFilters}
        loadingButton={loadingButton.value}
        clearFilters={clearFilters}
        searchButton={searchFromFirstPage}
      />
      <DataGrid
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        rows={rows}
        columns={columns}
        loading={loadingButton.value}
        paginationMode={tableMode}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        sortingMode={tableMode}
        sortModel={sortModel}
        onSortModelChange={handleSortModelChange}
        columnVisibilityModel={{
          id: false,
        }}
        disableColumnFilter
        disableColumnSelector
        autoHeight
        pagination
        filterMode={tableMode}
        rowCount={rowCount}
        pageSizeOptions={[limit]}
        getRowId={(row) => Number(row.id)}
        sx={{
          [`& .${gridClasses.cell}`]: {
            py: 1,
          },
          minHeight: '250px',
          [`.${gridClasses.columnHeaderTitle}`]: {
            fontWeight: 'bold !important',
          },
          '& .MuiDataGrid-cell:hover': {
            color: 'primary',
          },
        }}
      />

      <Dialog open={openDialog.get()} onClose={handleClose}>
        <DialogContent>
          {translate(
            'Do you want to use this dispatch to unify the current dispatch?'
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose}>
            {translate('Cancel')}
          </Button>
          <Button variant="contained" onClick={action}>
            {translate('Submit')}
          </Button>
        </DialogActions>
      </Dialog>
    </ErrorBoundary>
  )
}
